import { Drawer } from "antd";
import { FC } from "react";

import { useTypedSelector } from "@hooks";
import { ADVISOR_TERMINAL_NODE } from "@store/advisor";
import { IModal } from "@types";

import { AdvisorComposer } from "./AdvisorComposer";
import { AdvisorHeader } from "./AdvisorHeader";
import { AdvisorMessageList } from "./AdvisorMessageList";
import { useAdvisorSession } from "./useAdvisorSession";

export { AdvisorLauncherButton } from "./AdvisorLauncherButton";

/**
 * The AI advisor, as a full-height panel sliding in from the right. A Drawer (rather than the
 * centered @components/Modal its sibling panels use) so the conversation gets the vertical room
 * a scrolling chat needs — a fixed-width dialog doesn't fit a live, growing transcript well.
 */
const AdvisorChat: FC<IModal> = ({ isVisible, setIsVisible }) => {
  const { offer } = useTypedSelector(state => state?.offers);
  const { company } = useTypedSelector(state => state.auth?.user!);
  const { id: company_id } = company!;

  const {
    entries,
    choices,
    busy,
    loading,
    error,
    pending,
    draft,
    freeText,
    onChoose,
    onSubmitText,
    onDraftChange,
    onRevealPending,
    onRestart,
  } = useAdvisorSession({
    offerId: offer?.id,
    companyId: company_id,
    active: isVisible,
  });

  const lastAdvisorEntry = [...entries]
    .reverse()
    .find(entry => entry.role === "advisor");
  const isComplete =
    lastAdvisorEntry?.role === "advisor" &&
    lastAdvisorEntry.payload.node_id === ADVISOR_TERMINAL_NODE &&
    !pending;

  return (
    <Drawer
      title={<AdvisorHeader isComplete={isComplete} onRestart={onRestart} />}
      placement="right"
      width={560}
      visible={isVisible}
      onClose={() => setIsVisible(false)}
      destroyOnClose={false}
      bodyStyle={{
        padding: 0,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <AdvisorMessageList
        entries={entries}
        choices={choices}
        busy={busy}
        loading={loading}
        pending={pending}
        error={error}
        onChoose={onChoose}
        onRevealPending={onRevealPending}
        onRestart={onRestart}
      />
      <AdvisorComposer
        freeText={freeText}
        draft={draft}
        busy={busy}
        onDraftChange={onDraftChange}
        onSubmit={() => freeText && onSubmitText(freeText.submit_choice_id)}
      />
    </Drawer>
  );
};

export default AdvisorChat;
